import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './ui/table';

/**
 * TableComponent - Componente de tabela genérico e reutilizável
 * 
 * @param {Array} columns - Array de colunas: [{ key: 'name', label: 'Nome', render: (value, row) => ..., className: '', cellClassName: (value, row) => string }]
 * @param {Array} data - Dados da tabela
 * @param {Function} onEdit - Callback para editar: (row) => void. Sem editTo vira botão; com editTo é chamado no clique simples
 * @param {Function} editTo - Função que retorna URL para editar: (row) => string. Renderiza link real (abre em nova aba)
 * @param {Function} onDelete - Callback para excluir: (row) => void
 * @param {Function} onPrint - Callback para imprimir: (row) => void
 * @param {Function} customActions - Função que retorna JSX para ações customizadas: (row) => JSX
 * @param {boolean} pagination - Se deve mostrar paginação
 * @param {number} currentPage - Página atual
 * @param {number} perPage - Items por página
 * @param {number} totalItems - Total de items
 * @param {Function} onPageChange - Callback ao mudar página: (page) => void
 * @param {Array} filters - Filtros disponíveis
 * @param {Function} onFilterChange - Callback ao mudar filtro
 * @param {Function} onSearch - Callback de busca
 * @param {Function} onKeyDown - Callback de tecla pressionada
 * @param {Function} getRowClassName - Função para classe customizada da linha: (row) => string
 * @param {boolean} showTotals - Se deve mostrar linha de totais
 * @param {Object} totals - Objeto com valores totais
 * @param {string} emptyMessage - Mensagem quando vazio
 * @param {boolean} rounded - Se deve aplicar bordas arredondadas (padrão: true)
 * @param {boolean} bordered - Se deve aplicar borda (padrão: true)
 * @param {string} tableClassName - Classes extras na <table> (ex.: table-fixed para larguras de coluna)
 * @param {string} actionColClass - Classes da coluna "Ações" (largura fixa alinhada entre duas tabelas)
 */
export default function TableComponent({
    columns,
    data,
    onEdit,
    editTo,
    onDelete,
    onPrint,
    customActions,
    pagination = false,
    currentPage = 1,
    perPage = 10,
    totalItems = 0,
    onPageChange,
    filters = [],
    onFilterChange,
    onSearch,
    onKeyDown,
    getRowClassName,
    showTotals = false,
    totals = null,
    emptyMessage = 'Nenhum registro encontrado',
    rounded = true,
    bordered = true,
    tableClassName = '',
    actionColClass = '',
}) {
    // Scroll para o topo quando a página mudar
    useEffect(() => {
        if (pagination) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [currentPage, pagination]);

    const renderCellValue = (column, row) => {
        const value = row[column.key];

        if (column.render) {
            return column.render(value, row);
        }

        return value !== null && value !== undefined ? value : '-';
    };

    const renderPagination = () => {
        if (!pagination || totalItems === 0) return null;

        const lastPage = Math.ceil(totalItems / perPage);
        const from = (currentPage - 1) * perPage + 1;
        const to = Math.min(currentPage * perPage, totalItems);

        const pages = [];
        const maxButtons = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
        let endPage = Math.min(lastPage, startPage + maxButtons - 1);

        if (endPage - startPage < maxButtons - 1) {
            startPage = Math.max(1, endPage - maxButtons + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                <div className="text-sm text-gray-600">
                    Mostrando <span className="font-medium">{from}</span> a{' '}
                    <span className="font-medium">{to}</span> de{' '}
                    <span className="font-medium">{totalItems}</span> resultados
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onPageChange && onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Anterior
                    </button>

                    {startPage > 1 && (
                        <>
                            <button
                                onClick={() => onPageChange && onPageChange(1)}
                                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                1
                            </button>
                            {startPage > 2 && <span className="px-2 text-gray-500">...</span>}
                        </>
                    )}

                    {pages.map((page) => (
                        <button
                            key={page}
                            onClick={() => onPageChange && onPageChange(page)}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${page === currentPage
                                ? 'bg-primary-600 text-white'
                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                        >
                            {page}
                        </button>
                    ))}

                    {endPage < lastPage && (
                        <>
                            {endPage < lastPage - 1 && <span className="px-2 text-gray-500">...</span>}
                            <button
                                onClick={() => onPageChange && onPageChange(lastPage)}
                                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {lastPage}
                            </button>
                        </>
                    )}

                    <button
                        onClick={() => onPageChange && onPageChange(currentPage + 1)}
                        disabled={currentPage === lastPage}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Próximo
                    </button>
                </div>
            </div>
        );
    };

    // Clique simples executa onEdit sem navegar; Ctrl/Cmd/clique do meio seguem o link (nova aba)
    const handleEditClick = (e, row) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        onEdit(row);
    };

    const hasActions = onEdit || editTo || onDelete || onPrint || customActions;

    return (
        <div className={`bg-white overflow-hidden ${bordered ? 'border border-gray-200' : ''} ${rounded ? 'rounded-lg' : ''}`}>
            <Table className={tableClassName}>
                <TableHeader>
                    <TableRow>
                        {columns.map((column) => (
                            <TableHead
                                key={column.key}
                                className={[column.align === 'right' ? 'text-right' : '', column.colClass || ''].filter(Boolean).join(' ')}
                            >
                                {column.label}
                            </TableHead>
                        ))}
                        {hasActions && (
                            <TableHead className={['text-right', actionColClass].filter(Boolean).join(' ')}>
                                Ações
                            </TableHead>
                        )}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {data.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={columns.length + (hasActions ? 1 : 0)} className="text-center py-8">
                                <p className="text-gray-500">{emptyMessage}</p>
                            </TableCell>
                        </TableRow>
                    ) : (
                        <>
                            {data.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className={getRowClassName ? getRowClassName(row) : ''}
                                    onKeyDown={onKeyDown ? (e) => onKeyDown(e, row) : undefined}
                                >
                                    {columns.map((column) => {
                                        const value = row[column.key];
                                        const cellClass = column.cellClassName
                                            ? (typeof column.cellClassName === 'function'
                                                ? column.cellClassName(value, row)
                                                : column.cellClassName)
                                            : (column.className || '');
                                        const mergedCell = [column.colClass, cellClass].filter(Boolean).join(' ');

                                        return (
                                            <TableCell
                                                key={column.key}
                                                className={mergedCell}
                                            >
                                                {renderCellValue(column, row)}
                                            </TableCell>
                                        );
                                    })}
                                    {hasActions && (
                                        <TableCell className={['text-right', actionColClass].filter(Boolean).join(' ')}>
                                            {customActions ? (
                                                customActions(row)
                                            ) : (
                                                <div className="flex items-center justify-end gap-2">
                                                    {editTo && (
                                                        <Link
                                                            to={editTo(row)}
                                                            onClick={onEdit ? (e) => handleEditClick(e, row) : undefined}
                                                            className="p-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                                            title="Editar"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </Link>
                                                    )}
                                                    {onEdit && !editTo && (
                                                        <button
                                                            onClick={() => onEdit(row)}
                                                            className="p-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                                            title="Editar"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                    {onDelete && (
                                                        <button
                                                            onClick={() => onDelete(row)}
                                                            className="p-2 text-gray-600 hover:text-red-600 border border-gray-300 rounded-lg hover:bg-red-50 transition-colors"
                                                            title="Excluir"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                    {onPrint && (
                                                        <button
                                                            onClick={() => onPrint(row)}
                                                            className="p-2 text-primary-600 hover:text-primary-700 border border-gray-300 rounded-lg hover:bg-primary-50 transition-colors"
                                                            title="Visualizar"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}

                            {/* Totals Row */}
                            {showTotals && totals && (
                                <TableRow className="bg-gray-50 font-semibold">
                                    {columns.map((column, idx) => (
                                        <TableCell key={column.key} className={[column.colClass, column.className || ''].filter(Boolean).join(' ')}>
                                            {idx === 0 ? 'Total' : (totals[column.key] || '')}
                                        </TableCell>
                                    ))}
                                    {hasActions && <TableCell className={actionColClass} />}
                                </TableRow>
                            )}
                        </>
                    )}
                </TableBody>
            </Table>

            {renderPagination()}
        </div>
    );
}
